// Main Chat Engine Hook
// Entry point for AI Chat functionality

import { useRef, useCallback, useReducer, useEffect, useMemo } from 'react';
import { runConversation } from './engine';
import { HistoryManager } from './state/history';
import { createInitialState, sessionReducer, SessionAction } from './state/session';
import {
  createUserMessage,
  createAssistantMessage,
  createToolCallMessage,
  createToolResultMessage,
  createSystemMessage,
  filterForUI,
  groupToolResults
} from './types/messages';
import { buildStyleIndex, rebuildStyleIndexForFile } from '../../ai/tools/styleOps';
import { updateInlinedCss } from '../../services/editor';

// What the bubble says when images were sent with no words of their own.
const describeAttachments = (attachments) =>
  attachments.length === 1 ? attachments[0].name : `${attachments.length} images`;

export function useChatEngine({
  domain,
  fileList,
  setFileList,
  dbRef,
  iframeRef,
  saveFile,
  onFilesChanged
}) {
  const [state, dispatch] = useReducer(sessionReducer, createInitialState());
  const historyRef = useRef(null);
  const initializedRef = useRef(false);

  // Lazy init history manager
  const getHistory = useCallback(() => {
    if (!historyRef.current) {
      historyRef.current = new HistoryManager(domain, dbRef);
    }
    return historyRef.current;
  }, [domain, dbRef]);

  // Add message helper
  const addMessage = useCallback((message) => {
    dispatch({ type: SessionAction.ADD_MESSAGE, payload: message });
  }, []);

  // Auto-save history on every message. The unpaired-tool-call problem is handled
  // where it belongs, in save(), by trimming a dangling call off the tail.
  // Suppressing the save for the whole turn instead meant a reload mid-turn lost
  // everything including the user's own prompt, while the files the tools had
  // already written stayed changed - a worse trade than the one it avoided.
  useEffect(() => {
    if (state.initialized && state.messages.length > 0) {
      getHistory().save(state.messages);
    }
  }, [state.messages, state.initialized, getHistory]);

  // CSS is swapped into its inlined style tag here; HTML pages are patched by the editor via onFilesChanged
  const handleFileChanged = useCallback(async (path, content) => {
    if (path.endsWith('.css')) {
      updateInlinedCss(iframeRef.current.contentWindow.document, path, content);
      const newIndex = await rebuildStyleIndexForFile(path, content, { ...state.styleIndex });
      dispatch({ type: SessionAction.SET_STYLE_INDEX, payload: newIndex });
    }

    if (onFilesChanged) {
      await onFilesChanged([path]);
    }
  }, [iframeRef, state.styleIndex, onFilesChanged]);

  // Initialize chat
  const initializeChat = useCallback(async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    dispatch({ type: SessionAction.SET_LOADING, payload: true });

    // Load history
    const history = await getHistory().load();
    const hasRealMessages = history.some(m => m.type === 'user' || m.type === 'assistant');

    if (hasRealMessages) {
      dispatch({ type: SessionAction.SET_MESSAGES, payload: history });
      addMessage(createSystemMessage('Continuing session...'));
    } else {
      addMessage(createSystemMessage('I\'m your AI assistant. What would you like to change about your website?'));
    }

    // Build style index
    try {
      const index = await buildStyleIndex(fileList, dbRef);
      dispatch({ type: SessionAction.SET_STYLE_INDEX, payload: index });
    } catch {
      // Style indexing failed silently
    }

    dispatch({ type: SessionAction.SET_INITIALIZED, payload: true });
    dispatch({ type: SessionAction.SET_LOADING, payload: false });
  }, [getHistory, addMessage, fileList, dbRef]);

  // Submit message
  const handleSubmit = useCallback(async (input, attachments = []) => {
    const userInput = input.trim();
    if ((!userInput && !attachments.length) || state.loading) return;

    dispatch({ type: SessionAction.SET_LOADING, payload: true });

    // Build message with selected element and attachment context
    const contextLines = [];
    const displayParts = [];

    if (state.selectedElement) {
      contextLines.push(`Selected element: ${state.selectedElement.selector}`);
      contextLines.push(`HTML: ${state.selectedElement.html}`);
      displayParts.push(`🎯 ${state.selectedElement.selector}`);
      dispatch({ type: SessionAction.CLEAR_SELECTED_ELEMENT });
    }

    attachments.forEach(({ url }) => contextLines.push(`Attached image: ${url}`));

    // The bubble renders the images itself, so the URLs stay out of what is shown.
    displayParts.push(userInput || describeAttachments(attachments));

    const request = userInput ? `\n\nRequest: ${userInput}` : '';
    const fullMessage = contextLines.length
      ? `${contextLines.join('\n')}${request}`
      : userInput;
    const displayMessage = displayParts.join(': ');

    // Add user message immediately
    addMessage(createUserMessage(fullMessage, { displayContent: displayMessage, attachments }));

    // Build context for tools
    const toolContext = {
      fileList,
      setFileList,
      dbRef,
      iframeRef,
      saveFile,
      styleIndex: state.styleIndex,
      onFileChanged: handleFileChanged
    };

    // Callbacks for conversation
    const conversationCallbacks = {
      onToolCall: (toolCalls) => {
        addMessage(createToolCallMessage(toolCalls));
      },
      onToolResult: (toolCallId, name, result, displayText) => {
        addMessage(createToolResultMessage(toolCallId, name, result, displayText));
      },
      onAssistantMessage: (content) => {
        addMessage(createAssistantMessage(content));
      }
    };

    try {
      await runConversation(state.messages, fullMessage, toolContext, conversationCallbacks);
    } catch (err) {
      if (err.code === 'RATE_LIMITED') {
        addMessage(createAssistantMessage(
          'You\'re sending requests too quickly. Please wait a moment and try again.',
          { error: true, canRegenerate: true, originalMessage: fullMessage }
        ));
      } else if (err.code === 'FORBIDDEN') {
        addMessage(createAssistantMessage(
          'The AI assistant isn\'t available on your current plan.',
          { error: true }
        ));
      } else {
        addMessage(createAssistantMessage(
          `Error: ${err.message}`,
          { error: true, canRegenerate: true, originalMessage: fullMessage }
        ));
      }
    }

    dispatch({ type: SessionAction.SET_LOADING, payload: false });
  }, [state, addMessage, fileList, setFileList, dbRef, iframeRef, saveFile, handleFileChanged]);

  // Regenerate last response
  const handleRegenerate = useCallback(async (originalMessage) => {
    dispatch({ type: SessionAction.SET_LOADING, payload: true });

    const toolContext = {
      fileList,
      setFileList,
      dbRef,
      iframeRef,
      saveFile,
      styleIndex: state.styleIndex,
      onFileChanged: handleFileChanged
    };

    const conversationCallbacks = {
      onToolCall: (toolCalls) => addMessage(createToolCallMessage(toolCalls)),
      onToolResult: (id, name, result, text) => addMessage(createToolResultMessage(id, name, result, text)),
      onAssistantMessage: (content) => addMessage(createAssistantMessage(content))
    };

    try {
      await runConversation(state.messages, originalMessage, toolContext, conversationCallbacks);
    } catch (err) {
      addMessage(createAssistantMessage(`Error: ${err.message}`, { error: true }));
    }

    dispatch({ type: SessionAction.SET_LOADING, payload: false });
  }, [state, addMessage, fileList, setFileList, dbRef, iframeRef, saveFile, handleFileChanged]);

  // Clear chat
  const handleClearChat = useCallback(async () => {
    await getHistory().clear();
    dispatch({ type: SessionAction.CLEAR_CHAT });
    // Re-initialize with fresh session
    initializedRef.current = true;
    dispatch({ type: SessionAction.SET_INITIALIZED, payload: true });
    addMessage(createSystemMessage('I\'m your AI assistant. What would you like to change about your website?'));
  }, [getHistory, addMessage]);

  // Element picker
  const handleElementPicked = useCallback((selector, html) => {
    dispatch({
      type: SessionAction.SET_SELECTED_ELEMENT,
      payload: { selector, html }
    });
  }, []);

  const clearSelectedElement = useCallback(() => {
    dispatch({ type: SessionAction.CLEAR_SELECTED_ELEMENT });
  }, []);

  const displayMessages = useMemo(
    () => groupToolResults(filterForUI(state.messages)),
    [state.messages]
  );

  return {
    // State
    messages: state.messages,
    loading: state.loading,
    selectedElement: state.selectedElement,

    // Filtered and grouped messages for UI
    displayMessages,

    // Handlers
    initializeChat,
    handleSubmit,
    handleRegenerate,
    handleClearChat,
    handleElementPicked,
    clearSelectedElement
  };
}
